/**
 * Label media HeadLight customers print on. All dimensions in millimetres.
 *
 * Roll media print one label per page; sheet media lay labels out on a Letter page.
 * Sheet geometry follows Avery's published templates for the 5160/5163/5164 family
 * (the 52xx weatherproof stock shares the same layout).
 */

/**
 * @typedef {Object} MediaPreset
 * @property {string} Id
 * @property {string} Name
 * @property {'Roll'|'Sheet'} Kind
 * @property {boolean} [Continuous] - Cut from a continuous roll, not die-cut: a finished label image prints at the top rather than centred.
 * @property {number} LabelWidthMM
 * @property {number} LabelHeightMM
 * @property {number} DPI - Printer resolution, for whole-dot module sizing.
 * @property {'Side'|'Stack'} Layout - Code beside the text, or above it.
 * @property {number} [MarginMM] - Inner margin of each label.
 * @property {number} [PageWidthMM] - Sheets only.
 * @property {number} [PageHeightMM] - Sheets only.
 * @property {number} [Columns]
 * @property {number} [Rows]
 * @property {number} [TopMM]
 * @property {number} [LeftMM]
 * @property {number} [PitchXMM]
 * @property {number} [PitchYMM]
 */

const INCH = 25.4;

/** @type {Array<MediaPreset>} */
const MEDIA_PRESETS =
[
	{ Id: 'brother-62x100', Name: 'Brother QL 62 mm continuous, cut at 100 mm (DK-2205)', Kind: 'Roll', Continuous: true, LabelWidthMM: 62, LabelHeightMM: 100, DPI: 300, Layout: 'Stack', MarginMM: 3 },
	{ Id: 'brother-62x29', Name: 'Brother QL 62 × 29 mm address label (DK-1209)', Kind: 'Roll', LabelWidthMM: 62, LabelHeightMM: 29, DPI: 300, Layout: 'Side', MarginMM: 1.5 },
	{ Id: 'dymo-30370', Name: 'Dymo LabelWriter 30370 (54 × 59 mm)', Kind: 'Roll', LabelWidthMM: 54, LabelHeightMM: 59, DPI: 300, Layout: 'Stack', MarginMM: 2.5 },
	{ Id: 'square-52', Name: 'Square 52 × 52 mm', Kind: 'Roll', LabelWidthMM: 52, LabelHeightMM: 52, DPI: 203, Layout: 'Stack', MarginMM: 2 },
	{ Id: 'zebra-4x6', Name: 'Zebra 4″ × 6″ (GK420t and similar, 203 dpi)', Kind: 'Roll', LabelWidthMM: 4 * INCH, LabelHeightMM: 6 * INCH, DPI: 203, Layout: 'Stack', MarginMM: 4 },
	{ Id: 'avery-5160', Name: 'Avery 5160 / 5260 — 1″ × 2⅝″, 30 per sheet', Kind: 'Sheet', LabelWidthMM: 2.625 * INCH, LabelHeightMM: 1 * INCH, DPI: 600, Layout: 'Side', MarginMM: 1.5, PageWidthMM: 8.5 * INCH, PageHeightMM: 11 * INCH, Columns: 3, Rows: 10, TopMM: 0.5 * INCH, LeftMM: 0.1875 * INCH, PitchXMM: 2.75 * INCH, PitchYMM: 1 * INCH },
	{ Id: 'avery-5163', Name: 'Avery 5163 / 5263 — 2″ × 4″, 10 per sheet', Kind: 'Sheet', LabelWidthMM: 4 * INCH, LabelHeightMM: 2 * INCH, DPI: 600, Layout: 'Side', MarginMM: 3, PageWidthMM: 8.5 * INCH, PageHeightMM: 11 * INCH, Columns: 2, Rows: 5, TopMM: 0.5 * INCH, LeftMM: 0.15625 * INCH, PitchXMM: 4.1875 * INCH, PitchYMM: 2 * INCH },
	{ Id: 'avery-5164', Name: 'Avery 5164 / 5264 — 3⅓″ × 4″, 6 per sheet', Kind: 'Sheet', LabelWidthMM: 4 * INCH, LabelHeightMM: (10 / 3) * INCH, DPI: 600, Layout: 'Stack', MarginMM: 4, PageWidthMM: 8.5 * INCH, PageHeightMM: 11 * INCH, Columns: 2, Rows: 3, TopMM: 0.5 * INCH, LeftMM: 0.15625 * INCH, PitchXMM: 4.1875 * INCH, PitchYMM: (10 / 3) * INCH },
];

/**
 * @param {string} pId
 * @return {MediaPreset|null}
 */
function getMediaPreset(pId)
{
	return MEDIA_PRESETS.find((pPreset) => pPreset.Id === pId) || null;
}

module.exports =
{
	MEDIA_PRESETS,
	getMediaPreset,
};
